import type { QuoteSessionEnrichment, RoofSegment } from "@/api/types";

const TRUSTED_EPC_CONFIDENCE = new Set(["high", "medium"]);

function titleCaseFirst(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

/** "Semi-Detached" → "Semi-detached". Later hyphenated parts stay lowercase. */
export function formatBuiltForm(builtForm: string): string {
  return builtForm
    .trim()
    .split(/(\s+|-)/)
    .map((part, index) => {
      if (part === "-" || /^\s+$/.test(part)) return part;
      if (index === 0) return titleCaseFirst(part);
      return part.toLowerCase();
    })
    .join("");
}

export function formatPropertyLabel(
  builtForm?: string | null,
  propertyType?: string | null,
): string | null {
  const form = builtForm?.trim() ?? "";
  const type = propertyType?.trim() ?? "";
  if (!form && !type) return null;

  const prettyForm = form ? formatBuiltForm(form) : null;
  if (prettyForm && type) {
    if (form.toLowerCase().includes(type.toLowerCase())) return prettyForm;
    return `${prettyForm} ${type.toLowerCase()}`;
  }

  return prettyForm ?? titleCaseFirst(type);
}

/** "South-West" → "South-west". */
export function formatRoofFacing(facing?: string | null): string | null {
  const trimmed = facing?.trim();
  if (!trimmed) return null;
  return trimmed
    .split("-")
    .map((part, index) => (index === 0 ? titleCaseFirst(part) : part.toLowerCase()))
    .join("-");
}

export function largestRoofFacing(
  segments: Array<Pick<RoofSegment, "facing" | "area_meters2">> | null | undefined,
): string | null {
  if (!segments?.length) return null;

  let best: { facing: string; area: number } | null = null;
  for (const segment of segments) {
    const facing = segment.facing?.trim();
    if (!facing) continue;
    const area = Number(segment.area_meters2);
    const safeArea = Number.isFinite(area) ? area : -1;
    if (!best || safeArea > best.area) {
      best = { facing, area: safeArea };
    }
  }

  return best ? formatRoofFacing(best.facing) : null;
}

export function isTrustedEpcMatch(confidence?: string | null): boolean {
  return TRUSTED_EPC_CONFIDENCE.has((confidence ?? "").trim().toLowerCase());
}

export function formatStartingPointFacts(input: {
  enrichment?: QuoteSessionEnrichment | null;
  roofFacing?: string | null;
}): string | null {
  const trusted = isTrustedEpcMatch(input.enrichment?.epc_match_confidence);
  const parts: string[] = [];

  if (trusted) {
    const label = formatPropertyLabel(
      input.enrichment?.property_built_form,
      input.enrichment?.property_type,
    );
    if (label) parts.push(label);
  }

  const facing = formatRoofFacing(input.roofFacing);
  if (facing) parts.push(`roof faces ${facing}`);

  if (trusted) {
    const rating = input.enrichment?.epc_rating?.trim();
    if (rating) parts.push(`current EPC ${rating.toUpperCase()}`);
  }

  return parts.length > 0 ? parts.join(" · ") : null;
}
